module Sm.Service.Storage

open System
open Browser.Types
open FsToolkit.ErrorHandling

open Fable.Core.JsInterop

open Sm.Core.Text
open Sm.Core.Interop
open Sm.Core.Interop.Operators
open Sm.Core.Option.Operators
open Sm.Core.Date

open Sm.Service.Database

type Environment = {
    AccountId: string
    ClientId: Guid
    DatabaseName: string
}

module Environment =
    let establish accountId =
        let databaseName = sprintf "%s-%s" NamePrefix accountId

        promise {
            let! app = databaseName |> writeStore AppStore

            let! clientId = promise {
                match! app.get("client-id") |> result<string option> with
                | Some id -> return Guid.Parse(id)
                | None ->
                    let id = Guid.NewGuid()
                    do! app.put(id, "client-id") |> result<unit>
                    return id
            }

            return {
                AccountId = accountId
                ClientId = clientId
                DatabaseName = databaseName
            }
        }

module LogicalClock =
    let epoch = dateUtc 1970 1 1

    let inline serialize x =
        //Fable treats int64 as BigInt, which is reasonable in most
        //circumstances, but problematic in others (it can not be serialized
        //directly to json etc). Clock values use the 'safe' integer range, so
        //it's reasonable to convert them to Number.
        Number.create x

    let inline parse x =
        BigInt.create x

    let now () =
        let seconds =
            (DateTime.UtcNow - epoch)
            |> fun x -> x.TotalSeconds
            |> (round >> int64)
        seconds <<< 16

    let sync a b =
        (max a b) + 1L

type VectorClock = {
    Local: int64
    Remote: Map<Guid, int64>
}

module VectorClock =
    let [<Literal>] RemoteClocksKey = "remote-clocks"

    let create () =
        {
            Local = LogicalClock.now ()
            Remote = Map.empty
        }

    let update values clock =
        let nextRemote =
            (clock.Remote, values)
            ||> Seq.fold (fun vec (clientId, clock) ->
                vec
                |> Map.change clientId (function
                    | None -> Some clock
                    | Some x -> Some (max x clock)
                )
            )
        { clock with Remote = nextRemote }

    let load env =
        promise {
            let! db = connect env.DatabaseName
            let tx = db.transaction([| RecordStore; AppStore |], !!Readonly)

            let rs = tx.objectStore(RecordStore)
            let app = tx.objectStore(AppStore)

            let! local = promise {
                let clockIndex = rs.index("clock")

                let! max =
                    clockIndex.openCursor(KeyRange.unbounded (), Prev) 
                    |> Cursor.first (fun c -> c.key |> LogicalClock.parse)

                return max <??> LogicalClock.now ()
            }

            let! remote =
                app.get(RemoteClocksKey) 
                |> result<obj option>
                |> Promise.map (function
                    | None -> Map.empty
                    | Some value ->
                        value
                        |> objectEntries
                        |> Seq.map (fun (name, x) -> Guid.Parse(name), LogicalClock.parse x)
                        |> Map
                )

            return {
                Local = local
                Remote = remote
            }
        }

    let save clock env =
        promise {
            let value =
                createObj [
                    for name, clock in clock.Remote |> Map.toSeq do
                        string name ==> LogicalClock.serialize clock
                ]

            let! app = env.DatabaseName |> writeStore AppStore
            do! app.put(value, RemoteClocksKey) |> result<unit>
        }

type Timestamp = {
    Date: DateOnly
    Time: TimeOnly
}

module Timestamp =
    let epoch = dateUtc 1970 1 1

    let fromDate (date: DateOnly) =
        {
            Date = DateOnly(date.Year, date.Month, date.Day)
            Time = TimeOnly.MinValue
        }

    let fromDateTime (date: DateTime) =
        let date = DateTime.SpecifyKind(date, DateTimeKind.Utc)
        {
            Date = DateOnly(date.Year, date.Month, date.Day)
            Time = TimeOnly(date.Hour, date.Minute, date.Second)
        }

    let inline toDateTime { Date = d; Time = t } =
        DateTime(d.Year, d.Month, d.Day, t.Hour, t.Minute, t.Second)

    let inline today () =
        fromDateTime DateTime.Today

    let inline now () =
        fromDateTime DateTime.Now

    let serialize t =
        let instant = DateTime (t.Date.Year, t.Date.Month, t.Date.Day, t.Time.Hour, t.Time.Minute, t.Time.Second, DateTimeKind.Utc)
        if instant < epoch then 0L
        else
            instant - epoch
            |> fun x -> x.TotalSeconds
            |> (floor >> int64)

    let deserialize (value: int64) =
        let instant = epoch.AddSeconds(max 0. (float value))
        fromDateTime instant

type Timestamp with
    static member Today = Timestamp.today ()
    static member Now = Timestamp.now ()

type Code = private Code of int
let (|CodeValue|) (Code value) = value

module Code =
    let [<Literal>] Min = 0
    let [<Literal>] Max = 15

    let ofInt x =
        if x >= Min && x <= Max then Some (Code x)
        else None

    let demand x =
        match ofInt x with
        | None -> failwithf "The value '%i is not representable as a %s" x (nameof Code)
        | Some x -> x

    let format (CodeValue x) =
        sprintf "%0X" x

    let parse = function
        | "0" -> ofInt 0
        | "1" -> ofInt 1
        | "2" -> ofInt 2
        | "3" -> ofInt 3
        | "4" -> ofInt 4
        | "5" -> ofInt 5
        | "6" -> ofInt 6
        | "7" -> ofInt 7
        | "8" -> ofInt 8
        | "9" -> ofInt 9
        | "a" | "A" -> ofInt 10
        | "b" | "B" -> ofInt 11
        | "c" | "C" -> ofInt 12
        | "d" | "D" -> ofInt 13
        | "e" | "E" -> ofInt 14
        | "f" | "F" -> ofInt 15
        | _ -> None

module Id =
    let create code timestamp (id: Guid) =
        let ts =
            timestamp
            |> Timestamp.serialize
            |> sprintf "%010X"

        let code = Code.format code
        let id = string id

        Guid.Parse(sprintf "%s%s-%s%s" code ts[0..6] ts[7..9] id[12..])

    let now code =
        let id = Guid.NewGuid()
        let timestamp = Timestamp.Now
        create code timestamp id

    let parse (id: Guid) =
        let parseHexNumber s =
            try 
                Int64.Parse(s, Globalization.NumberStyles.HexNumber) |> Some
            with _ ->
                None

        option {
            let id = string id

            let! ts = parseHexNumber (id[1..7] + id[9..11])
            let timestamp = Timestamp.deserialize ts

            let! code =
                id[0..0]
                |> Code.parse

            return code, timestamp
        }

module Lookup =
    let [<Literal>] dateMin = "00000000"
    let [<Literal>] dateMax = "99999999"

    type Info = {
        Code: Code option
        Date: DateOnly
    }

    let formatDate (date: DateOnly) =
        if date = DateOnly.MinValue then dateMin
        elif date = DateOnly.MaxValue then dateMax
        else sprintf "%0i%02i%02i" date.Year date.Month date.Day

    let format = 
        function
        | { Code = Some code; Date = date } -> $"{Code.format code}-{formatDate date}"
        | { Date = date } -> formatDate date

    let parse =
        let (|Code|_|) = Code.parse

        function
        | Match @"^([0-9a-fA-F])-(\d{4})(\d{2})(\d{2})$" [Code code; Int year; Int month; Int day] ->
            Some {
                Code = Some code
                Date = DateOnly(year, month, day)
            }
        | Match  @"^(\d{4})(\d{2})(\d{2})$" [Int year; Int month; Int day] ->
            Some {
                Code = None
                Date = DateOnly(year, month, day)
            }
        | _ ->
            None

module Document =
    let [<Literal>] TextField = "text"
    let [<Literal>] TrackerField = "tracker"
    let [<Literal>] ScopeField = "scope"
    let [<Literal>] ArchivedField = "archived"

    type Register = {
        Clock: int64
        Value: string option
        Client: Guid
    }

    type Content = {
        Id: Guid
        Fields: Map<string, Register>
    }

    let findField name content =
        content.Fields
        |> Map.tryFind name
        |> Option.bind (fun x -> x.Value)

    let parse localClient value = 
        option {
            let! id =
                value @ "id"
                |> Option.map (string >> Guid.TryParse >> snd)

            let! fields =
                value @ "fields"
                |> Option.map (fun fields ->
                    fields
                    |> objectEntries
                    |> Seq.choose (fun (name, value) ->
                        let (|JsClock|_|) o =
                            match o with
                            | JsNumber x -> Some (int64 x)
                            | _ -> None

                        //Alas, this used to be a very elegant top level match
                        //using active patterns for the array and object
                        //elements, but it triggered a combinatorial explosion
                        //in the pattern compiler (the generated JavaScript was
                        //about 100 megabytes!) so now we have to break it down
                        //and do some manual parsing.
                        let r = 
                            let parseProps props =
                                match Array.length props with
                                | 3 ->
                                    option {
                                        let! value = (|JsString|_|) props[0]
                                        let! clock = (|JsClock|_|) props[1]
                                        let! remoteClient = (|JsGuid|_|) props[2]
                                        return { Value = Some value; Clock = clock; Client = remoteClient }
                                    }
                                | 2 ->
                                    match props[0], props[1] with
                                    | JsString value, JsClock clock -> Some { Value = Some value; Clock = clock; Client = localClient }
                                    | JsClock clock, JsGuid remoteClient -> Some { Value = None; Clock = clock; Client = remoteClient }
                                    | _ -> None
                                | 1 ->
                                    option {
                                        let! clock = (|JsClock|_|) props[0]
                                        return { Value = None; Clock = clock; Client = localClient }
                                    }
                                | _ -> None

                            match value with
                            | JsArray props -> parseProps props
                            | map ->
                                match map @ "value", map @ "clock", map @ "client" with
                                | None, Some clock, None ->
                                    parseProps [| clock |]

                                | Some value, Some clock, None ->
                                    parseProps [| value; clock |]

                                | None, Some clock, Some remoteClient ->
                                    parseProps [| clock; remoteClient |]

                                | Some value, Some clock, Some remoteClient ->
                                    parseProps [| value; clock; remoteClient |]

                                | _ -> None

                        r |> Option.map(fun r -> name, r)
                    )
                )
                |> Option.map Map.ofSeq

            return { Id = id; Fields = fields }
        }

    let serializeFields localClient fields =
        createObj [
            for KeyValue(name, r) in fields do
                if name = ScopeField then
                    name ==> createObj [|
                        match r.Value with
                        | None -> ()
                        | Some v -> yield "value" ==> v

                        yield "clock" ==> LogicalClock.serialize r.Clock

                        if r.Client = localClient then ()
                        else yield "client" ==> string r.Client
                    |]
                else
                    name ==> [|
                        match r.Value with
                        | None -> ()
                        | Some v -> yield v

                        yield LogicalClock.serialize r.Clock

                        if r.Client = localClient then ()
                        else yield string r.Client
                    |]
        ]

    let serialize localClient content =
        let maxClock fields =
            (0L, fields)
            ||> Map.fold (fun c _ r ->
                max c r.Clock
            )

        let isArchived =
            content
            |> findField ArchivedField
            |> Option.isSome

        let lookup =
            let code, date =
                content.Id
                |> Id.parse
                |> Option.map (fun (c, t) -> Some c, t.Date)
                |> Option.defaultValue (None, Timestamp.Today.Date)

            Lookup.format {
                Code = code
                Date = date
            }

        let localClocks =
            ([], content.Fields)
            ||> Map.fold (fun c _ r ->
                if r.Client = localClient then r.Clock :: c
                else c
            )

        createObj [
            "id" ==> content.Id
            "clock" ==> LogicalClock.serialize (maxClock content.Fields)

            match localClocks with
            | [] -> ()
            | clocks ->
                let values = 
                    clocks
                    |> List.map LogicalClock.serialize
                    |> List.toArray
                "local" ==> values

            "activity" ==> lookup
            if not isArchived then
                "pending" ==> lookup

            "fields" ==> serializeFields localClient content.Fields
        ]

    let merge name theirs fields =
        let mutable applied = false

        let inline takeTheirs () =
            applied <- true
            Some theirs

        let fields =
            fields
            |> Map.change name (function
                | None -> takeTheirs ()
                | Some ours ->
                    if theirs.Clock = ours.Clock then
                        if theirs.Value > ours.Value then takeTheirs ()
                        else Some ours
                    elif theirs.Clock > ours.Clock then takeTheirs ()
                    else Some ours
            )

        fields, applied
